import {
  FieldType,
  IFieldDefinition,
  LayoutElement,
  isLayoutTableFieldElement,
} from "@entaconsulting/form";
import { Button, Stack, Typography } from "@mui/material";
import ConfirmationModal from "common/components/ConfirmationModal";
import EntaAccordion from "common/components/EntaAccordion";
import {
  PanelSections,
  SUPPORTED_SCHEMA_FIELDS_BY_TYPE,
} from "developer/edit/constants";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useFieldNameSuggestions from "developer/edit/hooks/useFieldNameSuggestions";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import useIsNewestField from "developer/edit/hooks/useIsNewestField";
import { isEmpty, toPath } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import NameField from "./commonInputFields/NameField";
import TypeField from "./schemaFields/TypeField";

const NameAndTypeSection: React.FC = () => {
  const { isExpanded, handleChangeIsExpanded, hide } = useIsExpanded(
    PanelSections.NameAndType
  );
  const [newName, setNewName] = useState<string>("");
  const [newType, setNewType] = useState<string>("");
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameHelperText, setNameHelperText] = useState<string>("");
  const fieldNameSuggestions = useFieldNameSuggestions();
  const [hasUnsupportedProperties, setHasUnsupportedProperties] =
    useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const {
    input: { value: initialValueName, name: nameName, onChange: onChangeName },
  } = useDynamicField<string>("element.name");
  const {
    input: { value: initialValueType, name: typeName, onChange: onChangeType },
  } = useDynamicField<FieldType>("fieldDef.type");
  const {
    input: { value: element },
  } = useDynamicField<LayoutElement>("element");
  const {
    input: { value: fieldDef },
  } = useDynamicField<IFieldDefinition>("fieldDef");

  const isNewestField = useIsNewestField(initialValueName);
  const isTableFieldEl = isLayoutTableFieldElement(element);

  const handleChangeName = useCallback((newNameValue: string) => {
    setNewName(newNameValue);
  }, []);

  const handleChangeType = useCallback((newType: string) => {
    setNewType(newType);
  }, []);

  const handleCancelChangeType = useCallback(() => {
    setHasUnsupportedProperties(false);
  }, []);

  const handleConfirmChangeType = useCallback(() => {
    onChangeType({ target: { value: newType } });
    handleChangeIsExpanded?.({}, false);
    setHasUnsupportedProperties(false);
  }, [handleChangeIsExpanded, newType, onChangeType]);

  const handleClickCancel = useCallback(() => {
    handleChangeIsExpanded?.({}, false);
    setNewName(initialValueName);
    setNewType(initialValueType);
  }, [handleChangeIsExpanded, initialValueName, initialValueType]);

  const handleClickSave = useCallback(
    (event: any) => {
      const hasChangedName = initialValueName !== newName;
      const hasChangedType = initialValueType !== newType;

      if (hasChangedName) {
        onChangeName({ target: { value: newName } });
      }

      if (hasChangedType) {
        const hasAllSupportedProperties = Object.keys(fieldDef).every((x) => {
          return SUPPORTED_SCHEMA_FIELDS_BY_TYPE[newType].find((y) => y === x);
        });

        if (!hasAllSupportedProperties) {
          setHasUnsupportedProperties(true);
          return;
        }

        handleConfirmChangeType();
      }
    },
    [
      fieldDef,
      handleConfirmChangeType,
      initialValueName,
      initialValueType,
      newName,
      newType,
      onChangeName,
    ]
  );

  useEffect(() => {
    if (!isInitialized) return;

    const isNameAlreadyExists = fieldNameSuggestions
      .filter((x) => x !== initialValueName)
      .some((x) => x === newName);

    if (isNameAlreadyExists) {
      setNameError(true);
      setNameHelperText("Name Already Exists");
    } else if (isEmpty(newName)) {
      setNameError(true);
      setNameHelperText("Required");
    } else {
      setNameError(false);
      setNameHelperText("");
    }
  }, [fieldNameSuggestions, initialValueName, isInitialized, newName]);

  useEffect(() => {
    setNewName(initialValueName);
    setIsInitialized(true);
  }, [initialValueName]);

  useEffect(() => {
    setNewType(initialValueType);
  }, [initialValueType]);

  const disableSaveButton =
    (initialValueName === newName && initialValueType === newType) ||
    nameError ||
    !isInitialized;

  // Deprecated the "select" field type, and replaced it with the "options"
  // field type in the visual editor. The "select" option has been removed.
  const type = initialValueType === "select" ? "options" : initialValueType;

  const title = toPath(initialValueName).join(" / ");

  if (hide) return null;

  return (
    <>
      <EntaAccordion
        title={title}
        type={type}
        expanded={isExpanded}
        defaultExpanded={isNewestField}
        onChange={handleChangeIsExpanded}
      >
        <Stack spacing={1}>
          <NameField
            onChange={handleChangeName}
            value={newName}
            name={nameName}
            error={nameError}
            helperText={nameHelperText}
          />
          {!isTableFieldEl && (
            <TypeField
              onChange={handleChangeType}
              value={newType}
              name={typeName}
              disabled={isTableFieldEl}
            />
          )}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            {!disableSaveButton && (
              <Button
                variant="text"
                color="inherit"
                size="small"
                onClick={handleClickCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={disableSaveButton}
              disableElevation
              onClick={handleClickSave}
            >
              OK
            </Button>
          </Stack>
        </Stack>
      </EntaAccordion>
      {hasUnsupportedProperties && (
        <ConfirmationModal
          open={hasUnsupportedProperties}
          cancelLabel="Cancel"
          confirmLabel="Yes, Continue"
          mainButtonType="primary"
          title="Confirm Change Element Type"
          onCancel={handleCancelChangeType}
          onConfirm={handleConfirmChangeType}
        >
          <Typography variant="body1" color="initial">
            There are some properties not supported in the new Field Type. Do
            you want to continue?
          </Typography>
        </ConfirmationModal>
      )}
    </>
  );
};

export default NameAndTypeSection;
